import {
  type ListBlockModel,
  ListBlockSchema,
  type ListType,
  ParagraphBlockModel,
} from '@blocksuite/affine-model';
import { focusTextModel } from '@blocksuite/affine-rich-text';
import type { AffineTextAttributes } from '@blocksuite/affine-shared/types';
import { matchModels, toNumberedList } from '@blocksuite/affine-shared/utils';
import type { BlockComponent } from '@blocksuite/std';
import {
  InlineMarkdownExtension,
  type InlineMarkdownMatchContext,
} from '@blocksuite/std/inline';

function getListMarkdownTarget({
  inlineEditor,
  inlineRange,
  pattern,
  prefixText,
}: InlineMarkdownMatchContext<AffineTextAttributes>) {
  if (inlineEditor.yTextString.slice(0, inlineRange.index).includes('\n')) {
    return null;
  }

  const match = prefixText.match(pattern);
  if (!match) return null;

  let type: ListType;
  if (match[2]) {
    type = 'numbered';
  } else if (match[3] || match[4]) {
    type = 'bulleted';
  } else if (match[5] || match[6]) {
    type = 'todo';
  } else {
    return null;
  }

  if (!inlineEditor.rootElement) return null;
  const blockComponent =
    inlineEditor.rootElement.closest<BlockComponent>('[data-block-id]');
  if (
    !blockComponent ||
    !matchModels(blockComponent.model, [ParagraphBlockModel])
  ) {
    return null;
  }

  return {
    blockComponent,
    checked: match[6] !== undefined,
    order: match[2],
    type,
  };
}

export const ListMarkdownExtension =
  InlineMarkdownExtension<AffineTextAttributes>({
    name: 'list',
    completionHint: {
      type: 'list',
      isEligible: props => getListMarkdownTarget(props) !== null,
      getTarget: props => getListMarkdownTarget(props)?.type ?? 'list',
    },
    // group 2: number
    // group 3: bullet
    // group 4: bullet
    // group 5: todo
    // group 6: todo checked
    pattern: /^((\d+\.)|(-)|(\*)|(\[ ?\])|(\[x\]))\s$/,
    action: props => {
      const target = getListMarkdownTarget(props);
      if (!target) return;

      const { inlineEditor, inlineRange } = props;
      const { blockComponent, checked, order: matchedOrder, type } = target;
      const { model, std, store } = blockComponent;

      if (type !== 'numbered') {
        const parent = store.getParent(model);
        if (!parent) return;
        const index = parent.children.indexOf(model);

        store.captureSync();
        inlineEditor.deleteText({
          index: 0,
          length: inlineRange.index,
        });
        const id = store.addBlock<ListBlockModel>(
          ListBlockSchema.model.flavour,
          {
            type: type,
            text: model.text?.clone(),
            children: model.children,
            ...(type === 'todo' ? { checked } : {}),
          },
          parent,
          index
        );
        store.deleteBlock(model, { deleteChildren: false });
        focusTextModel(std, id);
      } else {
        let order = parseInt(matchedOrder);
        if (!Number.isInteger(order)) order = 1;

        store.captureSync();
        inlineEditor.deleteText({
          index: 0,
          length: inlineRange.index,
        });

        const id = toNumberedList(std, model, order);
        if (!id) return;

        focusTextModel(std, id);
      }
    },
  });
